/**
 * A book's notes in the local store (decisions 62, 76; part 19e log, tables NR, NO and W′): one note record each, on a
 * chapter (anchored or on the whole chapter) or in the Notebook, with replies, Resolve and tombstones; and each chapter's
 * synopsis, kept on its entry (part 19c, table W). Every operation runs in the snapshot history's queue, as the chapter
 * operations do (part 08, principle 6), so a rename or a move that puts a whole entry back never lands between a note's
 * read and its write.
 */
import type { SessionWriteResult } from './edit-lock.ts';
import type { SnapshotHistory } from './history.ts';
import { StoreRecordError } from './records.ts';
import type { ChapterEntry, KeyGroup, NoteAnchor, NoteRecord, NoteShare } from './records.ts';
import type { InvalidRecord, Store, StoreFailure, WriteOp } from './store.types.ts';

/** How many fresh IDs a new note tries before giving up (NO13). */
export const MAX_NOTE_ID_TRIES = 8;

/** What goes between two versions of a note's text kept together (W5). */
export const KEPT_TOGETHER = '\n\n';

/** What {@link Notes} needs; everything with an effect is injected. */
export interface NotesOptions {
  readonly store: Store;
  /** The edit session's write (part 06): committed only while this tab holds the lock. */
  readonly write: (ops: readonly WriteOp[]) => Promise<SessionWriteResult>;
  /** This device's snapshot history, whose queue every operation runs in. */
  readonly history: SnapshotHistory;
  /** Milliseconds since the Unix epoch. */
  readonly now: () => number;
  /** A fresh ID. */
  readonly newId: () => string;
}

/**
 * A field the writer types in (table W′): a chapter's synopsis, or a note's text, or a Notebook note's title or group.
 */
export type NoteTarget =
  | { readonly kind: 'chapter'; readonly id: string; readonly field: 'synopsis' }
  | { readonly kind: 'note'; readonly id: string; readonly field: 'text' | 'title' | 'group' };

/** The one key of a field, for maps of edits. */
export function targetKey(target: NoteTarget): string {
  return `${target.kind}:${target.id}:${target.field}`;
}

/** Typing in one field (table W′): what was stored when it started (`base`), and the field's text now. */
export interface NoteEdit {
  readonly target: NoteTarget;
  readonly base: string;
  readonly value: string;
}

/** Not written: this tab lost the lock, or the store failed. */
type NotWritten = { readonly kind: 'stale' } | StoreFailure;

/**
 * A book's notes as the store holds them: every record, tombstones included, by when each was stored, then ID; and the
 * records the reader refused (NR7).
 */
export interface NoteListing {
  readonly notes: readonly NoteRecord[];
  readonly invalid: readonly InvalidRecord[];
}

/** What a new note is (NO1–NO5): where it sits, its words, and the thread it answers. */
export interface NewNote {
  readonly bookId: string;
  /** Its chapter; null in the Notebook. */
  readonly chapterId: string | null;
  /** Its passage (NO1); null on the whole chapter, in the Notebook and on a reply. */
  readonly anchor: NoteAnchor | null;
  readonly title: string;
  readonly group: string;
  readonly text: string;
  /** The thread's first note (NO5); null for a first note. */
  readonly parentId: string | null;
  /**
   * Part 38e (DN1): who may see a first note: Only me (`null`), or a key group of the book's share, by the share record's
   * ID. A reply takes its thread's, whatever this says.
   */
  readonly shared?: { readonly share: string; readonly group: KeyGroup } | null;
}

/**
 * Part 38e (table PN): one change a pull brings: a note to store when no record holds its ID; an invitee's new words for a
 * note held (PN12); a note deleted at the relay (PN9).
 */
export type PulledChange =
  | { readonly kind: 'create'; readonly note: NoteRecord }
  | { readonly kind: 'update'; readonly id: string; readonly text: string; readonly sent: string }
  /** Deep-fix r1, table TD′: an invitee's note `id`, held across share `share` as relay note `note`, deleted at the relay. */
  | { readonly kind: 'delete'; readonly id: string; readonly share: string; readonly note: string };

/**
 * What a change did: `stored` (created, updated, deleted), `held` (nothing to do: the ID already held, a delete already
 * applied, words already the same, or a note here the writer deleted, PN17), `refused` (no such note to update, or not
 * an invitee's).
 */
export type PulledResult = 'stored' | 'held' | 'refused';

/** The outcome of {@link Notes.applyPulled}: each change's result, in order; or nothing written. */
export type ApplyPulledResult = { readonly kind: 'applied'; readonly results: readonly PulledResult[] } | NotWritten;

/** The outcome of {@link Notes.create}: made, or the thread it answers is gone or a tombstone (NO11). */
export type CreateNoteResult = { readonly kind: 'created'; readonly note: NoteRecord } | { readonly kind: 'missing' } | NotWritten;

/** The outcome of {@link Notes.resolve} and {@link Notes.remove}: done; `missing` for no such standing note (NO11). */
export type NoteChangeResult = { readonly kind: 'done' } | { readonly kind: 'missing' } | NotWritten;

/**
 * The outcome of {@link Notes.write} (table W′): every edit applied in one write, with each field's stored text now
 * (by {@link targetKey}), how many fields kept another tab's text with this one's (W5), and how many fields' records were
 * gone (W1); or nothing written.
 */
export type WriteNotesResult =
  | { readonly kind: 'written'; readonly stored: ReadonlyMap<string, string>; readonly joined: number; readonly lost: number }
  | NotWritten;

/**
 * W2–W5: what a field holds after an edit: the typing when the field still holds what it started from, or already holds
 * it, or is a short field (a title or group: the last writer wins, decision 62); otherwise both, the stored text first.
 */
export function applyEdit(stored: string, edit: NoteEdit): { readonly text: string; readonly joined: boolean } {
  const short = edit.target.field === 'title' || edit.target.field === 'group';
  if (stored === edit.base || stored === edit.value || short) return { text: edit.value, joined: false };
  return { text: stored + KEPT_TOGETHER + edit.value, joined: true };
}

/** NR7's listing order: by when each was stored, then by ID. */
function byStoring(a: NoteRecord, b: NoteRecord): number {
  return a.storedAt - b.storedAt || Number(a.id > b.id) - Number(a.id < b.id);
}

/** Whether `note` stands: stored and not a tombstone. */
function standing(note: NoteRecord | undefined): note is NoteRecord {
  return note?.deletedAt === null;
}

/** Principle 7: `note` as a tombstone deleted `at`: its IDs, place, origin and times, no words. */
export function tombstone(note: NoteRecord, at: number): NoteRecord {
  return { ...note, anchor: null, title: '', group: '', author: '', text: '', resolved: false, deletedAt: at };
}

export class Notes {
  private readonly options: NotesOptions;

  constructor(options: NotesOptions) {
    this.options = options;
  }

  /** NR7: the book's notes in the order they were stored, tombstones included, and the refused records by key. */
  list(bookId: string): Promise<NoteListing | StoreFailure> {
    return this.options.history.exclusive(async () => {
      const { records, invalid } = await this.options.store.readWhere('notes', 'bookId', bookId);
      return { notes: [...records].sort(byStoring), invalid };
    });
  }

  /**
   * NO1–NO5, NO13: a new note of the writer's, Only me, open, now, under an ID no note has (readable or refused); a reply
   * only to a standing first note of the same book, on its chapter (`missing` otherwise).
   */
  create(draft: NewNote): Promise<CreateNoteResult> {
    return this.options.history.exclusive(async (): Promise<CreateNoteResult> => {
      const { store } = this.options;
      let { chapterId } = draft;
      let parent: NoteRecord | undefined;
      if (draft.parentId !== null) {
        parent = await store.read('notes', draft.parentId);
        if (!standing(parent) || parent.parentId !== null || parent.bookId !== draft.bookId) return { kind: 'missing' };
        chapterId = parent.chapterId;
      }
      for (let tries = 0; tries < MAX_NOTE_ID_TRIES; tries++) {
        const id = this.options.newId();
        if (await this.held(id)) continue;
        const at = this.options.now();
        const reply = draft.parentId !== null;
        // DN1: a reply takes its thread's visibility, share and thread (NR8: a share part exactly when not Only me); a
        // first note the one chosen.
        const chosen = parent === undefined ? (draft.shared ?? null) : null;
        const from = parent?.shared ?? (chosen === null ? undefined : { share: chosen.share, thread: null });
        const shared: NoteShare | undefined = from === undefined ? undefined : { share: from.share, note: id, thread: from.thread, author: null, epoch: 0, sent: null };
        const note: NoteRecord = {
          id,
          bookId: draft.bookId,
          chapterId,
          anchor: reply || chapterId === null ? null : draft.anchor,
          title: reply || chapterId !== null ? '' : draft.title,
          group: reply || chapterId !== null ? '' : draft.group,
          origin: 'writer',
          author: '',
          sourceKey: null,
          visibility: parent?.visibility ?? chosen?.group ?? 'only-me',
          parentId: draft.parentId,
          resolved: false,
          text: draft.text,
          writtenAt: at,
          storedAt: at,
          editedAt: at,
          deletedAt: null,
          ...(shared === undefined ? {} : { shared }),
        };
        const written = await this.options.write([{ kind: 'put', store: 'notes', value: note }]);
        return written.kind === 'committed' ? { kind: 'created', note } : written;
      }
      return { kind: 'failed', error: new Error('no free note ID') };
    });
  }

  /**
   * NO6: resolves (`resolved` true) or reopens a thread by its first note; `anchor`, when given, is where the note was
   * found now, written with it (C7: the writer acted on it). `missing` for a note gone, a tombstone or a reply.
   */
  resolve(noteId: string, resolved: boolean, anchor?: NoteAnchor | null): Promise<NoteChangeResult> {
    return this.options.history.exclusive(async (): Promise<NoteChangeResult> => {
      const note = await this.options.store.read('notes', noteId);
      if (!standing(note) || note.parentId !== null) return { kind: 'missing' };
      const placed = anchor === undefined || note.anchor === null ? note.anchor : anchor;
      return this.done(await this.options.write([{ kind: 'put', store: 'notes', value: { ...note, resolved, anchor: placed, editedAt: this.options.now() } }]));
    });
  }

  /**
   * NO8: deletes a note, leaving its tombstone (principle 7); a first note's replies too, in the same write. `missing` for
   * a note gone or already a tombstone.
   */
  remove(noteId: string): Promise<NoteChangeResult> {
    return this.options.history.exclusive(async (): Promise<NoteChangeResult> => {
      const { store } = this.options;
      const note = await store.read('notes', noteId);
      if (!standing(note)) return { kind: 'missing' };
      const at = this.options.now();
      const replies = note.parentId === null ? (await store.readWhere('notes', 'bookId', note.bookId)).records.filter((each) => each.parentId === note.id && each.deletedAt === null) : [];
      const ops = [note, ...replies].map((each): WriteOp => ({ kind: 'put', store: 'notes', value: tombstone(each, at) }));
      return this.done(await this.options.write(ops));
    });
  }

  /**
   * Table W′: applies every edit to its record as stored now, all in one write. Each record is read once and takes all its
   * fields' edits; a chapter in the trash takes them too (W6); a record gone, a tombstone, or another's note (a Word
   * note's words are its author's, decision 76) takes none (W1). A note edited gets `editedAt` and, when `anchors` holds
   * one for it and it is anchored, its anchor as found now (NO7). A record its reader refuses throws (part 06, R5), so
   * nothing is written over it.
   */
  write(edits: readonly NoteEdit[], anchors: ReadonlyMap<string, NoteAnchor | null> = new Map()): Promise<WriteNotesResult> {
    return this.options.history.exclusive(async (): Promise<WriteNotesResult> => {
      const entries = new Map<string, ChapterEntry | undefined>();
      const notes = new Map<string, NoteRecord | undefined>();
      const stored = new Map<string, string>();
      let joined = 0;
      let lost = 0;
      for (const edit of edits) {
        const { target } = edit;
        if (target.kind === 'chapter') {
          const entry = entries.has(target.id) ? entries.get(target.id) : await this.options.store.read('entries', target.id);
          if (entry === undefined) {
            entries.set(target.id, undefined);
            lost += 1;
            continue;
          }
          const next = applyEdit(entry[target.field], edit);
          entries.set(target.id, { ...entry, [target.field]: next.text });
          stored.set(targetKey(target), next.text);
          if (next.joined) joined += 1;
        } else {
          const read = notes.has(target.id) ? notes.get(target.id) : await this.options.store.read('notes', target.id);
          const note = standing(read) && read.origin === 'writer' && (target.field === 'text' || (read.chapterId === null && read.parentId === null)) ? read : undefined;
          if (note === undefined) {
            notes.set(target.id, undefined);
            lost += 1;
            continue;
          }
          const next = applyEdit(note[target.field], edit);
          const anchor = anchors.get(note.id);
          notes.set(target.id, { ...note, [target.field]: next.text, anchor: anchor === undefined || note.anchor === null ? note.anchor : anchor, editedAt: this.options.now() });
          stored.set(targetKey(target), next.text);
          if (next.joined) joined += 1;
        }
      }
      const ops: WriteOp[] = [
        ...[...entries.values()].flatMap((entry): WriteOp[] => (entry === undefined ? [] : [{ kind: 'put', store: 'entries', value: entry }])),
        ...[...notes.values()].flatMap((note): WriteOp[] => (note === undefined ? [] : [{ kind: 'put', store: 'notes', value: note }])),
      ];
      if (ops.length > 0) {
        const written = await this.options.write(ops);
        if (written.kind !== 'committed') return written;
      }
      return { kind: 'written', stored, joined, lost };
    });
  }

  /**
   * Table WN: what the relay holds of note `noteId` across its share, as this device now knows (its epoch, `sent`, and the
   * range sent with it: deep-fix r4, AR′), written
   * onto the note as stored now (an edit or a delete since is kept: the next run sends it, WN12). `missing` for no note
   * held across that share.
   */
  markShared(noteId: string, share: string, mark: Pick<NoteShare, 'epoch' | 'sent'> & { readonly range?: NoteShare['range'] | null }): Promise<NoteChangeResult> {
    return this.options.history.exclusive(async (): Promise<NoteChangeResult> => {
      const note = await this.options.store.read('notes', noteId);
      if (note?.shared?.share !== share) return { kind: 'missing' };
      // Deep-fix r4, AR′: the range sent with it when `mark` names one, none when it says null; else the one held stays.
      const range = mark.range === null ? undefined : (mark.range ?? note.shared.range);
      const { share: held, note: relayId, thread, author, epoch, sent } = note.shared;
      if (epoch === mark.epoch && sent === mark.sent && range?.from === note.shared.range?.from && range?.to === note.shared.range?.to) return { kind: 'done' };
      const next = { share: held, note: relayId, thread, author, epoch: mark.epoch, sent: mark.sent, ...(range === undefined ? {} : { range }) };
      return this.done(await this.options.write([{ kind: 'put', store: 'notes', value: { ...note, shared: next } }]));
    });
  }

  /**
   * Table PN: a pull's changes, all in one write against the notes as stored now. A create only where no record holds the
   * ID (NO13); an update only of a standing invitee's note (its words and `sent`; a tombstone here stays one, PN17); a
   * delete (deep-fix r1, table TD′) only of an invitee's note held across that share under that relay ID, leaving its
   * tombstone with `sent` deleted; its replies stay (the relay's word never destroys the writer's words). Anything else
   * a delete names is `held`, untouched.
   */
  applyPulled(changes: readonly PulledChange[]): Promise<ApplyPulledResult> {
    return this.options.history.exclusive(async (): Promise<ApplyPulledResult> => {
      const { store } = this.options;
      const at = this.options.now();
      const writes = new Map<string, NoteRecord>();
      const current = async (id: string): Promise<NoteRecord | undefined> => writes.get(id) ?? (await store.read('notes', id));
      const results: PulledResult[] = [];
      for (const change of changes) {
        if (change.kind === 'create') {
          if (writes.has(change.note.id) || (await this.held(change.note.id))) results.push('held');
          else {
            writes.set(change.note.id, change.note);
            results.push('stored');
          }
          continue;
        }
        const note = await current(change.id);
        if (change.kind === 'delete') {
          // TD′: only the invitee's note the relay named, across its share; never the writer's, another share's or an Only-me one.
          if (note?.origin !== 'invitee' || note.shared?.share !== change.share || note.shared.note !== change.note || note.shared.sent === 'deleted') {
            results.push('held');
            continue;
          }
          writes.set(note.id, { ...(note.deletedAt === null ? tombstone(note, at) : note), shared: { ...note.shared, sent: 'deleted' } });
          results.push('stored');
          continue;
        }
        if (note?.origin !== 'invitee' || note.shared === undefined) results.push('refused');
        else if (note.deletedAt !== null || (note.text === change.text && note.shared.sent === change.sent)) results.push('held');
        else {
          writes.set(note.id, { ...note, text: change.text, editedAt: at, shared: { ...note.shared, sent: change.sent } });
          results.push('stored');
        }
      }
      if (writes.size > 0) {
        const written = await this.options.write([...writes.values()].map((value): WriteOp => ({ kind: 'put', store: 'notes', value })));
        if (written.kind !== 'committed') return written;
      }
      return { kind: 'applied', results };
    });
  }

  /**
   * MU2: the whole-chapter note an earlier build's typing in chapter `chapterId`'s notes goes to, made empty under the
   * chapter's ID (MG2's) when no note holds that ID and the chapter's entry is stored; `missing` when the chapter is gone
   * (MU3); `done` when the note is already there.
   */
  ensureChapterNote(chapterId: string): Promise<NoteChangeResult> {
    return this.options.history.exclusive(async (): Promise<NoteChangeResult> => {
      const { store } = this.options;
      if (await this.held(chapterId)) return { kind: 'done' };
      const entry = await store.read('entries', chapterId);
      if (entry === undefined) return { kind: 'missing' };
      const at = this.options.now();
      const note: NoteRecord = {
        id: chapterId,
        bookId: entry.bookId,
        chapterId,
        anchor: null,
        title: '',
        group: '',
        origin: 'writer',
        author: '',
        sourceKey: null,
        visibility: 'only-me',
        parentId: null,
        resolved: false,
        text: '',
        writtenAt: null,
        storedAt: at,
        editedAt: at,
        deletedAt: null,
      };
      return this.done(await this.options.write([{ kind: 'put', store: 'notes', value: note }]));
    });
  }

  /** NO13: whether a note record, readable or refused, holds `id`. */
  private held(id: string): Promise<boolean> {
    return this.options.store.read('notes', id).then(
      (note) => note !== undefined,
      (error: unknown) => {
        if (error instanceof StoreRecordError) return true;
        throw error;
      },
    );
  }

  /** A write's result as a change's. */
  private done(written: SessionWriteResult): NoteChangeResult {
    return written.kind === 'committed' ? { kind: 'done' } : written;
  }
}
